<script setup lang="ts">
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import axios from 'axios';
import { useAuthStore } from '@/stores/useAuthStore';
import { isTrustedRedirect } from '@/functions/trustedRedirect';
import { backend_url } from '@/config/config';
import dartsHubApi from '@/axios/dartsHubApi';
import dartsHubLogo from '@/assets/dartshub-logo-blanc.png';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const email = ref('');
const password = ref('');
const loading = ref(false);
const error = ref('');
const showPassword = ref(false);
const showForgotPassword = ref(false);
const forgotEmail = ref('');
const forgotLoading = ref(false);

const redirectTarget = route.query.redirect as string | undefined;
// Present uniquement pour une connexion "Se connecter avec DartsHub" initiee par un
// partenaire externe (ex: DartUp) : bascule login/register/Google vers l'emission d'un
// token d'identite restreint au lieu du token interne complet.
const clientId = route.query.client_id as string | undefined;

const cancel = () => {
  router.push('/');
};

const googleLogin = () => {
  const params = new URLSearchParams();
  if (redirectTarget) params.set('redirect', redirectTarget);
  if (clientId) params.set('client_id', clientId);
  const qs = params.toString();
  window.location.href = `${backend_url}/auth/google${qs ? `?${qs}` : ''}`;
};

const redirectDomain = redirectTarget
  ? (() => { try { return new URL(redirectTarget).hostname; } catch { return redirectTarget; } })()
  : null;

const registerQuery = (() => {
  const params = new URLSearchParams();
  if (redirectTarget) params.set('redirect', redirectTarget);
  if (clientId) params.set('client_id', clientId);
  const qs = params.toString();
  return qs ? `/register?${qs}` : '/register';
})();

const login = async () => {
  error.value = '';
  loading.value = true;
  try {
    const form = new URLSearchParams();
    form.append('username', email.value);
    form.append('password', password.value);

    if (clientId) {
      form.append('client_id', clientId);
      if (redirectTarget) form.append('redirect_uri', redirectTarget);
    } else {
      const scopeValue = redirectTarget
        ? (() => { try { return new URL(redirectTarget).hostname; } catch { return null; } })()
        : 'dartshub.fr';
      if (scopeValue) form.append('scope', scopeValue);
    }

    const { data } = await axios.post(`${backend_url}/auth/login`, form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    if (clientId) {
      if (data.consent_required) {
        router.push({
          path: '/partners/consent',
          query: { consent_token: data.consent_token, client_name: data.client_name },
        });
        return;
      }
      // Flow partenaire : identite minimale uniquement, jamais de token interne complet.
      const fragment = new URLSearchParams({ token: data.identity_token }).toString();
      window.location.href = `${redirectTarget}#${fragment}`;
      return;
    }

    const { data: user } = await axios.get(`${backend_url}/users/me`, {
      headers: { Authorization: `Bearer ${data.access_token}` },
    });

    authStore.setAuthData({
      token: data.access_token,
      refreshToken: data.refresh_token,
      email: user.email,
      name: user.name,
      firstname: user.firstname,
      nickname: user.nickname || '',
      profile_picture: user.profile_picture || '',
      role: user.role,
      scopes: user.scopes || [],
      userId: user.id,
    });

    if (redirectTarget && await isTrustedRedirect(redirectTarget)) {
      const fragment = new URLSearchParams({
        token: data.access_token,
        refresh: data.refresh_token,
        email: user.email,
        name: user.name,
        firstname: user.firstname,
        nickname: user.nickname || '',
        profile_picture: user.profile_picture || '',
        role: user.role,
        scopes: user.scopes || [],
        userId: user.id,
      }).toString();
      window.location.href = `${redirectTarget}#${fragment}`;
      return;
    }

    if (user.scopes?.includes('super_admin')) {
      router.push('/admin/users');
    } else {
      router.push('/profile');
    }
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Email ou mot de passe incorrect';
  } finally {
    loading.value = false;
  }
};

const sendForgotPassword = async () => {
  forgotLoading.value = true;
  try {
    await dartsHubApi.post('/auth/forgot-password', { email: forgotEmail.value, scope: 'DartsHub' });
    toast.success('Un email avec ton nouveau mot de passe temporaire t\'a été envoyé.');
    showForgotPassword.value = false;
    forgotEmail.value = '';
  } catch {
    toast.error('Erreur lors de l\'envoi.');
  } finally {
    forgotLoading.value = false;
  }
};
</script>

<template>
  <div class="dh-page">
    <div class="dh-wrapper">

      <!-- ── En-tête DartsHub ── -->
      <div class="dh-header">
        <svg class="dh-target-bg" viewBox="-140 -140 280 280" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="0" cy="0" r="140" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.35)"
            stroke-width="1.2" />
          <circle cx="0" cy="0" r="124" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1" />
          <circle cx="0" cy="0" r="82" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.35)" stroke-width="1" />
          <circle cx="0" cy="0" r="72" fill="none" stroke="rgba(255,255,255,0.28)" stroke-width="1" />
          <circle cx="0" cy="0" r="16" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" />
          <circle cx="0" cy="0" r="6" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="1.2" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(0)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(18)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(36)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(54)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(72)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(90)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(108)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(126)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(144)" />
          <line x1="0" y1="-140" x2="0" y2="140" stroke="rgba(255,255,255,0.18)" stroke-width="0.8"
            transform="rotate(162)" />
        </svg>

        <div class="dh-header-content">
          <div class="dh-brand">
            <img :src="dartsHubLogo" alt="DartsHub" class="dh-brand-logo" />
          </div>
          <div class="dh-tricolor-bar">
            <span></span><span></span><span></span>
          </div>
          <p class="dh-subtitle">
            {{ redirectTarget ? 'Connexion via DartsHub' : 'Connexion à votre espace' }}
          </p>
        </div>
      </div>

      <!-- ── Corps ── -->
      <div class="dh-body">
        <template v-if="!showForgotPassword">
          <form class="dh-form" @submit.prevent="login">

            <div class="dh-field">
              <label>Email</label>
              <input v-model="email" type="email" placeholder="votre@email.fr" autocomplete="email" required
                class="dh-input" />
            </div>

            <div class="dh-field">
              <label>Mot de passe</label>
              <div class="dh-password-wrap">
                <input v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="••••••••"
                  autocomplete="current-password" required class="dh-input dh-input-pw" />
                <button type="button" class="dh-pw-toggle" tabindex="-1" @click="showPassword = !showPassword">
                  <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
                </button>
              </div>
            </div>

            <div v-if="error" class="dh-msg fd-msg--error">{{ error }}</div>

            <button type="submit" class="dh-btn fd-btn-primary" :disabled="loading">
              <i v-if="loading" class="fa-solid fa-spinner fa-spin" />
              <span>{{ loading ? 'Connexion…' : 'Se connecter' }}</span>
            </button>
          </form>

          <div class="dh-divider"><span>ou</span></div>

          <button class="dh-btn fd-btn-google" @click="googleLogin">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continuer avec Google
          </button>

          <div class="dh-links-row">
            <button class="dh-link" @click="showForgotPassword = true">
              Mot de passe oublié ?
            </button>
            <RouterLink :to="registerQuery" class="dh-link">Créer un compte</RouterLink>
          </div>
        </template>

        <!-- Mot de passe oublié -->
        <div v-else class="dh-forgot-panel">
          <div class="dh-forgot-header">
            <button class="dh-link dh-back-link" @click="showForgotPassword = false">
              <i class="fa-solid fa-arrow-left" /> Retour
            </button>
            <span class="dh-forgot-title">Mot de passe oublié</span>
          </div>
          <div class="dh-field">
            <label>Votre email</label>
            <input v-model="forgotEmail" type="email" placeholder="votre@email.fr" class="dh-input" />
          </div>
          <button class="dh-btn fd-btn-ghost" :disabled="forgotLoading" @click="sendForgotPassword">
            <i v-if="forgotLoading" class="fa-solid fa-spinner fa-spin" />
            <span>{{ forgotLoading ? 'Envoi…' : 'Envoyer un nouveau mot de passe' }}</span>
          </button>
        </div>

        <button class="dh-btn dh-btn-cancel" @click="cancel">Annuler</button>

        <!-- Indicateur SSO -->
        <div v-if="redirectTarget" class="dh-sso-notice">
          <i class="fa-solid fa-shield-halved" />
          Connexion sécurisée pour <strong>{{ redirectDomain }}</strong>
        </div>
      </div>

      <!-- ── Footer ── -->
      <div class="dh-footer">
        <span>Authentification sécurisée par</span>
        <strong>&nbsp;DartsHub</strong>
        <span class="dh-footer-sep">·</span>
        <RouterLink to="/contact?from=login" class="dh-footer-link">Contact</RouterLink>
      </div>

    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

.dh-page {
  min-height: 100svh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: var(--bg);
}

.dh-wrapper {
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', 'Segoe UI', sans-serif;
  background: var(--bg-card);
  color: var(--text);
  border-radius: 20px;
  overflow: hidden;
  width: 420px;
  max-width: 100%;
  box-shadow: 0 8px 40px color-mix(in srgb, var(--primary) 10%, transparent);
}

/* ── Header ── */
.dh-header {
  background: linear-gradient(145deg, var(--dh-header-from) 0%, var(--primary) 60%, var(--dh-header-to) 100%);
  position: relative;
  text-align: center;
  overflow: hidden;
}

.dh-target-bg {
  position: absolute;
  top: 50%;
  right: -30px;
  transform: translateY(-50%);
  width: 240px;
  height: 240px;
  pointer-events: none;
  opacity: 0.7;
}

.dh-header-content {
  position: relative;
  z-index: 1;
  padding: 22px 28px 16px;
}

.dh-brand {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
}

.dh-brand-logo {
  height: 2.25rem;
  width: auto;
}

.dh-tricolor-bar {
  display: flex;
  height: 4px;
  width: 110px;
  margin: 0 auto 12px;
  border-radius: 10px;
  overflow: hidden;
  gap: 3px;
}

.dh-tricolor-bar span {
  flex: 1;
  border-radius: 10px;
}

.dh-tricolor-bar span:nth-child(1) {
  background: var(--accent);
}

.dh-tricolor-bar span:nth-child(2) {
  background: var(--dh-bar-2);
}

.dh-tricolor-bar span:nth-child(3) {
  background: var(--secondary);
}

.dh-subtitle {
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 14px;
  letter-spacing: 0.2px;
  font-weight: 300;
}

/* ── Corps ── */
.dh-body {
  padding: 24px 28px;
  flex: 1;
}

.dh-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.dh-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dh-field label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
}

.dh-input {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid var(--border);
  border-radius: 12px;
  background: var(--bg-input);
  color: var(--text);
  font-size: 0.92rem;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  box-sizing: border-box;
}

.dh-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 10%, transparent);
  background: var(--bg-card);
}

.dh-password-wrap {
  position: relative;
}

.dh-input-pw {
  padding-right: 2.8rem;
}

.dh-pw-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.dh-pw-toggle:hover {
  color: var(--text-muted);
}

/* ── Boutons ── */
.dh-btn {
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.dh-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.dh-btn-primary {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px color-mix(in srgb, var(--primary) 28%, transparent);
}

.dh-btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, var(--primary-hover) 0%, color-mix(in srgb, var(--primary) 60%, #000) 100%);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--primary) 36%, transparent);
  transform: translateY(-1px);
}

.dh-btn-ghost {
  background: transparent;
  color: var(--text-muted);
  border: 2px solid var(--border);
  margin-top: 8px;
}

.dh-btn-ghost:hover:not(:disabled) {
  background: var(--bg);
  border-color: var(--border);
  color: var(--text-muted);
}

.dh-btn-cancel {
  background: transparent;
  color: var(--text-muted);
  border: none;
  font-size: 0.82rem;
  font-weight: 500;
  margin-top: 4px;
  opacity: 0.7;
}

.dh-btn-cancel:hover {
  opacity: 1;
  text-decoration: underline;
}

/* ── Messages ── */
.dh-msg {
  font-size: 0.83rem;
  font-weight: 500;
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  margin: 0;
}

.dh-msg--error {
  background: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}

/* ── Liens ── */
.dh-links-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 14px;
}

.dh-link {
  background: none;
  border: none;
  color: var(--primary);
  font-weight: 600;
  font-size: 0.83rem;
  font-family: inherit;
  text-decoration: none;
  cursor: pointer;
  padding: 0;
}

.dh-link:hover {
  text-decoration: underline;
}

/* ── Mot de passe oublié ── */
.dh-forgot-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dh-forgot-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.dh-forgot-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text);
}

.dh-back-link {
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* ── SSO notice ── */
.dh-sso-notice {
  margin-top: 14px;
  text-align: center;
  font-size: 0.78rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #eff3ff;
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  padding: 8px 12px;
}

.dh-sso-notice strong {
  color: var(--primary);
}

/* ── Footer ── */
.dh-footer {
  background: var(--bg-input);
  border-top: 1px solid var(--border);
  padding: 10px 16px;
  text-align: center;
  font-size: 0.72rem;
  color: var(--text-muted);
  font-family: 'Poppins', 'Segoe UI', sans-serif;
}

.dh-footer strong {
  color: var(--primary);
  font-weight: 600;
}

.dh-footer-sep {
  margin: 0 6px;
  color: #cbd5e1;
}

.dh-footer-link {
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;
}

.dh-footer-link:hover {
  text-decoration: underline;
}

/* ── Divider ── */
.dh-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 0 10px;
  color: var(--text-muted);
  font-size: 0.78rem;
}
.dh-divider::before,
.dh-divider::after {
  content: '';
  flex: 1;
  border-top: 1px solid var(--border);
}

/* ── Bouton Google ── */
.dh-btn-google {
  background: var(--bg-card);
  color: #3c4043;
  border: 2px solid #dadce0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.dh-btn-google:hover {
  background: #f8f9fa;
  border-color: #c6c9cc;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
}
</style>
